/* Routes, defined once and typed.
 *
 * TanStack Router rather than React Router: a link to a route that does not
 * exist, or a missing search parameter, is a compile error here rather than a
 * blank screen. That matters more than usual in this repo, where the same
 * discipline already runs through branded ids and strict compiler settings.
 *
 * Search parameters are validated, not read as strings. `?token=` arrives from
 * an email — it is external input like any other, and `coding.md` wants it
 * parsed at the boundary rather than hand-checked downstream.
 */

import { LEGAL_DOCUMENTS } from '@eventuley/contracts';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { AccountScreen } from '../features/account/index.js';
import { ForgotPasswordScreen } from '../features/auth/routes/forgot-password.js';
import { ResetPasswordScreen } from '../features/auth/routes/reset-password.js';
import { SignInScreen } from '../features/auth/routes/sign-in.js';
import { SignUpScreen } from '../features/auth/routes/sign-up.js';
import { VerifyScreen } from '../features/auth/routes/verify.js';
import { CalendarScreen, calendarSearchSchema } from '../features/calendar/index.js';
import { EventScreen, EventsScreen, eventsSearchSchema } from '../features/events/index.js';
import { InventoryScreen } from '../features/inventory/index.js';
import { LegalDocumentScreen } from '../features/legal/index.js';
import { MoneyScreen } from '../features/money/index.js';
import {
  AcceptInvitationScreen,
  AskToJoinScreen,
  ChooseWorkspaceScreen,
  CreateWorkspaceScreen,
  SettingsScreen,
} from '../features/organisations/index.js';
import { PeopleScreen } from '../features/people/index.js';
import { BillingScreen, PlansScreen } from '../features/plans/index.js';
import { TasksScreen } from '../features/tasks/index.js';
import { VenuesScreen } from '../features/venues/index.js';
import { isInAppPath } from '../shared/lib/in-app-path.js';
import { NotBuiltScreen } from './not-built.js';
import { RouteError } from './route-error.js';
import { WorkspaceShell } from './workspace-shell.js';

const rootRoute = createRootRoute({
  component: Outlet,
  /* The outermost boundary — for a failure with no route to belong to, such as
   * one thrown resolving the tree itself. The per-route boundaries that keep a
   * broken screen from blanking the app are `defaultErrorComponent` on the
   * router below; this one alone was not enough, and read as though it were. */
  errorComponent: RouteError,
});

/* Nothing lives at the root yet. Sending it to sign-in rather than rendering a
 * blank page keeps the entry point honest until there is a dashboard. */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/sign-in' });
  },
});

/* Public, and outside every shell — STORY-048.
 *
 * The readers who most need this page are the ones with no session: somebody
 * deciding whether to sign up, and a data subject exercising a right under the
 * Privacy Policy who is not a customer and never will be. Putting a login in
 * front of a contract is the same defect as not publishing it.
 *
 * The slug is matched against the closed set rather than passed through. An
 * unrecognised one resolves to `undefined` and the screen says so — it must
 * never fall back to another document, because showing the Terms to somebody
 * who asked for the DPA is worse than showing nothing. */
const legalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/$document',
  component: function LegalRoute() {
    const { document } = legalRoute.useParams();
    return <LegalDocumentScreen slug={LEGAL_DOCUMENTS.find((known) => known === document)} />;
  },
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-up',
  component: SignUpScreen,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  /* Where somebody was headed before they were asked to sign in — STORY-009.
   * Parsed, not trusted: `isInAppPath` is what stops this screen becoming an
   * open redirect, and the rule lives in one module because the test route tree
   * has to enforce the same one.
   *
   * Caught rather than thrown: a mangled or hostile value drops the redirect
   * and shows an ordinary sign-in screen, which is the safe outcome. An error
   * boundary here would be a worse experience for the same input. */
  validateSearch: z.object({
    redirect: z.string().refine(isInAppPath).optional().catch(undefined),
  }),
  component: SignInScreen,
});

const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify',
  /* Both optional, and for different reasons: `token` is absent when the screen
   * is reached straight after signing up, and `email` is absent when arriving
   * from the emailed link. `catch` keeps a mangled URL — a mail client that
   * broke the link across lines, say — from throwing before the screen can say
   * anything useful. */
  validateSearch: z.object({
    token: z.string().optional().catch(undefined),
    email: z.string().optional().catch(undefined),
  }),
  component: VerifyScreen,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordScreen,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  /* Optional and caught rather than required. A missing or mangled token must
   * reach the screen so it can say "that link no longer works" — throwing in
   * the router would show an error boundary instead, which says less and looks
   * like a fault on our side. */
  validateSearch: z.object({ token: z.string().optional().catch(undefined) }),
  component: ResetPasswordScreen,
});

const nextRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/next',
  component: ChooseWorkspaceScreen,
});

const createWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/next/create',
  component: CreateWorkspaceScreen,
});

/* The third route out of the fork — STORY-008. A sibling of creating rather
 * than a panel on the chooser, because it has a form, a submitted state, and a
 * refusal of its own, and all three need somewhere to live that a person can
 * come back to. */
const askToJoinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/next/join',
  component: AskToJoinScreen,
});

/* The URL shape every in-workspace screen inherits, and it mirrors the API's
 * `/orgs/{organisationId}` — where tenant context gets set from STORY-005
 * onward. Cheap to choose now, expensive once a dozen screens sit under it.
 *
 * A LAYOUT route: the shell renders once and the destination fills its outlet,
 * so navigating between destinations does not remount the navigation. */
const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orgs/$organisationId',
  component: WorkspaceShell,
});

/* Calendar is home — STORY-039. Landing on the bare workspace path sends you
 * there rather than rendering an empty outlet. */
const workspaceIndexRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/orgs/$organisationId/calendar',
      params: { organisationId: params.organisationId },
    });
  },
});

/* The eight destinations. Every one is registered even though only its
 * placeholder exists: `frontend-react.md` and STORY-039 both refuse a dead link
 * in the navigation, and a route that 404s is a dead link with extra steps. */
const DESTINATION_PATHS = ['venues', 'people', 'tasks', 'money', 'inventory', 'settings'] as const;

/* The events list, registered on its own rather than in the loop below,
 * because it is the one destination with search parameters — STORY-040. Its
 * filters live in the URL so a view can be linked and returned to, and
 * `validateSearch` is what makes that a parsed, typed thing rather than
 * whatever a query string happened to contain.
 *
 * Every field `.catch()`es, so a hand-edited or stale link degrades to the
 * default view rather than throwing. A filter nobody can read is not worth a
 * broken screen. */
/* Registered on its own rather than in the loop below, because it carries a
 * search parameter — the month, so a selection survives a reload and a link can
 * be shared. `.catch()`ed, so a hand-edited or stale link degrades to this
 * month rather than throwing: a month nobody can read is not worth a broken
 * screen. */
const calendarRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'calendar',
  validateSearch: calendarSearchSchema,
  component: CalendarScreen,
});

const eventsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'events',
  validateSearch: eventsSearchSchema,
  component: EventsScreen,
});

/* One Event, under the list it belongs to. A child of the workspace layout
 * like every other in-workspace screen — STORY-011. */
const eventRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'events/$eventId',
  component: EventScreen,
});

const destinationRoutes = DESTINATION_PATHS.map((path) =>
  createRoute({
    getParentRoute: () => workspaceRoute,
    path,
    /* Settings is built — STORY-006. The rest are placeholders until their own
     * story replaces them, which is what keeps the scope boundary on screen. */
    /* Settings is STORY-006 and Events is STORY-011. The rest are placeholders
     * until their own story replaces them, which is what keeps the scope
     * boundary on screen rather than only in a document. */
    component:
      path === 'settings'
        ? SettingsScreen
        : path === 'venues'
          ? VenuesScreen
          : path === 'people'
            ? PeopleScreen
            : path === 'money'
              ? MoneyScreen
              : path === 'tasks'
                ? TasksScreen
                : path === 'inventory'
                  ? InventoryScreen
                  : () => <NotBuiltScreen destination={path} />,
  }),
);

/* Deliberately NOT a workspace destination — STORY-010. The account belongs to
 * no Organisation, so it sits outside the shell entirely. */
const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  component: AccountScreen,
});

/* Billing, and it IS a workspace destination — STORY-032.
 *
 * The checkout's return urls point here. They used to point at
 * `/settings/billing`, which named no workspace and therefore could not be a
 * workspace route at all — so it did not exist, and a customer who had just
 * paid landed on "Not found".
 *
 * `checkout` is optional and caught, because arriving without it is the
 * ordinary case: somebody opening billing from the navigation has no such
 * parameter, and a mangled value must reach the screen rather than throw. */
const billingRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'billing',
  validateSearch: z.object({ checkout: z.string().optional().catch(undefined) }),
  component: BillingScreen,
});

/* Public, and deliberately outside the workspace shell: STORY-031 makes the
 * plans page a sales page as much as an account screen, so it renders with no
 * session at all. The workspace it shows usage for is NAMED in the query and
 * authorised by the server — ADR-0010, the same rule as a path parameter. */
const plansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/plans',
  validateSearch: z.object({ workspace: z.string().optional().catch(undefined) }),
  component: PlansScreen,
});

/* Reached from an emailed link and from the chooser. `token` is optional and
 * caught: arriving with none is the ordinary "I was told a code" case rather
 * than an error, and a mangled link must reach the screen so it can say
 * something useful instead of showing an error boundary. */
const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invitations/accept',
  validateSearch: z.object({ token: z.string().optional().catch(undefined) }),
  component: AcceptInvitationScreen,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  legalRoute,
  signUpRoute,
  signInRoute,
  verifyRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  nextRoute,
  createWorkspaceRoute,
  askToJoinRoute,
  accountRoute,
  plansRoute,
  acceptInvitationRoute,
  workspaceRoute.addChildren([
    workspaceIndexRoute,
    ...destinationRoutes,
    calendarRoute,
    eventsRoute,
    eventRoute,
    billingRoute,
  ]),
]);

export function buildRouter() {
  return createRouter({
    routeTree,
    defaultPreload: 'intent',
    /* Every route gets its own boundary, which is what makes the claim on the
     * root route true — `frontend-react.md`: "one broken feature does not blank
     * the app."
     *
     * Errors travel to the NEAREST ancestor boundary. With one declared only at
     * the root, a throw in any screen reached it and replaced the entire tree,
     * navigation included, leaving the browser's back button as the only way
     * out of a broken screen. `route-error.test.tsx` mounts this router with a
     * screen that throws and asserts the drawer is still there; it fails
     * without this line. */
    defaultErrorComponent: RouteError,
  });
}

/* Registers the route tree with the library's types, which is what makes
 * `<Link to="/sign-up">` checked and `to="/sign-upp"` a compile error. */
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
